import { FC, useEffect, useRef, useState } from 'react';

import type { Nullable } from 'types';
import { isAiGalleryImage } from 'utils/campaign';
import { getFileName } from 'utils/clickme';
import { ImageType } from 'utils/constants/banner';
import { Settings } from 'utils/constants/dynamicSettings';

import generateImageButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_design/generate_image_button_click';
import bannerImageFirstUploadButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/banner_image_first_upload_button_click';
import { Button, ScrollableContainer, Text, Tooltip, TooltipHover, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { HyperstarSparkleFilledSize24, ImageOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import LocalStorageWrapper from 'bloko/common/storage/LocalStorageWrapper';

import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';
import { useShowAiImageGeneration } from 'hooks/useShowAiImageGeneration';

import { AiIcon } from 'components/common/BannerImg/GalleryModal/AiIcon/AiIcon';
import { loadGalleryImages } from 'components/common/BannerImg/GalleryModal/loadGalleryImages';
import { loadImage } from 'components/common/BannerImg/loadImage';
import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';

import { setIsAiGenerationModalVisible } from 'store/models/aiImageGeneration';
import { setIsCropImageModalVisible } from 'store/models/createBanner';
import { ImageCreatedBy, setIsGalleryModalVisible } from 'store/models/gallery';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/GalleryPreview/GalleryPreview.module.less';

const PREVIEW_ITEMS_COUNT = 5;
const IS_AI_IMAGE_GENERATION_TIP_WAS_SHOWN = 'IS_AI_IMAGE_GENERATION_TIP_WAS_SHOWN';

const Trls = {
    preview: 'AdCreate.AdForm.BannerImg.gallery.preview',
    gallery: 'AdCreate.AdForm.BannerImg.gallery.open',
    generation: 'AdCreate.AdForm.BannerImg.generationMagritte',
    tooltip: 'AdCreate.AdForm.BannerImg.generation.tip',
    aiIconTip: 'AdCreate.AdForm.BannerImg.aiIcon.tip',
};

interface ImageItemProps {
    originalImageId: number;
    previewUrl: string;
    cropImageUrl: string;
    createdBy: Nullable<ImageCreatedBy>;
    onImageClick: (originalImageId: number, cropImageUrl: string) => void;
    tooltipText: string;
}

const ImageItemWithTooltip: FC<ImageItemProps> = ({
    originalImageId,
    previewUrl,
    cropImageUrl,
    createdBy,
    onImageClick,
    tooltipText,
}) => {
    const activatorRef = useRef<HTMLDivElement>(null);

    return (
        <div className={styles.imageContainer}>
            <img
                className={styles.image}
                src={previewUrl}
                onClick={() => onImageClick(originalImageId, cropImageUrl)}
            />
            {isAiGalleryImage(createdBy) && (
                <>
                    <div className={styles.previewAiIcon} ref={activatorRef}>
                        <AiIcon />
                    </div>
                    <TooltipHover placement="right-center" activatorRef={activatorRef} maxWidth={280}>
                        <Text>{tooltipText}</Text>
                    </TooltipHover>
                </>
            )}
        </div>
    );
};

const GalleryPreviewComponent: FC = () => {
    const [isShowInfo, setShowInfo] = useState(!LocalStorageWrapper.getItem(IS_AI_IMAGE_GENERATION_TIP_WAS_SHOWN));
    const bannerVacancyId = useSelector((state) => state.createBanner.bannerVacancyId);
    const defaultImages = useSelector((state) => state.gallery.defaultImages);

    const dispatch = useDispatch();
    const trl = useTrls();
    const settings = useSettings();
    const { isGtS } = useBreakpoint();

    const generateButtonActivatorRef = useRef<HTMLButtonElement>(null);

    const isAiImageGenerationEnabled = settings(Settings.AiImageGenerationEnabled);
    const isAiImageGenerationAvailable = useShowAiImageGeneration();

    const showAiGeneration = bannerVacancyId && isAiImageGenerationAvailable;

    useEffect(() => {
        dispatch(loadGalleryImages());
    }, [dispatch, bannerVacancyId]);

    useEffect(() => {
        if (isAiImageGenerationEnabled) {
            LocalStorageWrapper.setItem(IS_AI_IMAGE_GENERATION_TIP_WAS_SHOWN, 'true');
        }
    }, [isAiImageGenerationEnabled]);

    if (defaultImages.length === 0) {
        return null;
    }

    const images = defaultImages[0].defaultImagesDto.slice(0, PREVIEW_ITEMS_COUNT);

    const selectFromGallery = () => {
        bannerImageFirstUploadButtonClick({
            buttonName: 'choose_from_gallery',
        });
        dispatch(setIsGalleryModalVisible(true));
    };

    const generateImage = () => {
        generateImageButtonClick();
        dispatch(setIsAiGenerationModalVisible(true));
        setShowInfo(false);
    };

    const handleImageClick = (originalImageId: number, cropImageUrl: string) => {
        const fileName = getFileName(originalImageId, cropImageUrl);
        void dispatch(
            loadImage({
                imageType: ImageType.Gallery,
                fileName,
                id: originalImageId,
                callBack: () => dispatch(setIsCropImageModalVisible(true)),
            })
        );
    };

    return (
        <>
            {isGtS ? (
                <div className={styles.imagesContainer}>
                    {images.map(({ originalImageId, previewUrl, cropImageUrl, createdBy }) => (
                        <ImageItemWithTooltip
                            key={originalImageId}
                            originalImageId={originalImageId}
                            previewUrl={previewUrl}
                            cropImageUrl={cropImageUrl}
                            createdBy={createdBy}
                            onImageClick={handleImageClick}
                            tooltipText={trl(Trls.aiIconTip)}
                        />
                    ))}
                </div>
            ) : (
                <ScrollableContainer>
                    <ScrollableContainer.Gallery>
                        <div className={styles.imagesContainer}>
                            {images.map(({ originalImageId, previewUrl, cropImageUrl, createdBy }) => (
                                <ScrollableContainer.Item key={originalImageId}>
                                    <ImageItemWithTooltip
                                        originalImageId={originalImageId}
                                        previewUrl={previewUrl}
                                        cropImageUrl={cropImageUrl}
                                        createdBy={createdBy}
                                        onImageClick={handleImageClick}
                                        tooltipText={trl(Trls.aiIconTip)}
                                    />
                                </ScrollableContainer.Item>
                            ))}
                        </div>
                    </ScrollableContainer.Gallery>
                </ScrollableContainer>
            )}
            <VSpacing default={24} />
            <div className={styles.buttonsContainer}>
                {showAiGeneration && (
                    <>
                        <Button
                            mode="secondary"
                            style="neutral"
                            size="large"
                            onClick={generateImage}
                            icon={<HyperstarSparkleFilledSize24 initialColor="negative" />}
                            data-qa="generate-image-button"
                            stretched
                            spaceBetween={false}
                            ref={generateButtonActivatorRef}
                        >
                            {trl(Trls.generation)}
                        </Button>
                        <Tooltip
                            visible={isShowInfo}
                            activatorRef={generateButtonActivatorRef}
                            placement="right-center"
                            onClose={() => setShowInfo(false)}
                        >
                            <Text>{trl(Trls.tooltip)}</Text>
                        </Tooltip>
                    </>
                )}
                <Button
                    mode="secondary"
                    style="neutral"
                    size="large"
                    stretched
                    spaceBetween={false}
                    icon={<ImageOutlinedSize24 />}
                    onClick={selectFromGallery}
                    data-qa="select-from-gallery"
                >
                    {trl(Trls.gallery)}
                </Button>
            </div>
        </>
    );
};

const GalleryPreview = withErrorBoundary(GalleryPreviewComponent);
export { GalleryPreview };
